import { Injectable } from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';

const DENSIFY_METERS = 10;
const MAX_POINTS_PER_GEOM = 2000;

/**
 * MapHeatmapContributionsService
 *
 * Supplements the point-based heatmap with contributions from bid-item
 * line and polygon as-built geometries. Each non-point geometry is
 * densified into a collection of virtual points (spaced ~DENSIFY_METERS
 * apart) whose heatmap weight equals the entry's per-feature quantity.
 * This way a long line or large polygon influences the heatmap
 * proportionally to its spatial extent and recorded quantity instead of
 * contributing zero points.
 *
 * The contributions live on a dedicated client-side FeatureLayer that is
 * activated only in heatmap mode and torn down otherwise.
 */
@Injectable({ providedIn: 'root' })
export class MapHeatmapContributionsService {
  private layer: FeatureLayer | null = null;
  // Tracks user intent separately from whether a layer has been built yet.
  // If the user toggles heatmap ON before geometries have loaded, we still
  // remember the intent so a later update() actually builds the layer.
  private wantsActive = false;

  isActive(): boolean {
    return this.wantsActive;
  }

  /**
   * Toggle visibility of the contributions layer without tearing it down.
   * Used to tie the heatmap to the user's "As-builts" layer-visibility
   * checkbox — when as-builts are hidden, the density derived from them
   * should be hidden too.
   */
  setVisible(visible: boolean): void {
    if (this.layer) {
      this.layer.visible = visible;
    }
  }

  activate(mapView: __esri.MapView, geometries: unknown[]): void {
    this.wantsActive = true;
    this.tearDownLayer(mapView);
    const points = this.buildVirtualPoints(geometries);
    // eslint-disable-next-line no-console
    console.log(`[MapHeatmapContributions] activate: ${points.length} virtual points from ${(geometries || []).length} geometries`);
    if (points.length === 0) {
      // No layer yet — but wantsActive stays true so update() will retry
      // once geometries arrive via the subscription.
      return;
    }
    this.layer = this.buildLayer(points);
    // Place beneath other layers — this is background density, not markers.
    mapView.map.add(this.layer, 0);
  }

  deactivate(mapView: __esri.MapView): void {
    this.wantsActive = false;
    this.tearDownLayer(mapView);
  }

  update(mapView: __esri.MapView, geometries: unknown[]): void {
    if (!this.wantsActive) {
      return;
    }
    // Rebuild even if no layer existed yet — this is how we recover from
    // an early toggle that hit empty geometries.
    this.tearDownLayer(mapView);
    const points = this.buildVirtualPoints(geometries);
    // eslint-disable-next-line no-console
    console.log(`[MapHeatmapContributions] update: ${points.length} virtual points from ${(geometries || []).length} geometries`);
    if (points.length === 0) {
      return;
    }
    this.layer = this.buildLayer(points);
    mapView.map.add(this.layer, 0);
  }

  private tearDownLayer(mapView: __esri.MapView): void {
    if (this.layer) {
      mapView.map.remove(this.layer);
      this.layer.destroy();
      this.layer = null;
    }
  }

  private buildLayer(points: Graphic[]): FeatureLayer {
    return new FeatureLayer({
      id: 'heatmap-contributions-layer',
      source: points,
      objectIdField: 'OBJECTID',
      geometryType: 'point',
      spatialReference: { wkid: 102100 },
      fields: [
        { name: 'OBJECTID', type: 'oid' },
        { name: 'weight', type: 'double' },
      ],
      renderer: {
        type: 'heatmap',
        field: 'weight',
        radius: 16,
        minDensity: 0,
        maxDensity: 0.05,
        colorStops: [
          { ratio: 0,    color: 'rgba(0, 0, 0, 0)' },
          { ratio: 0.2,  color: 'rgba(255, 235, 59, 0.6)' },
          { ratio: 0.5,  color: 'rgba(255, 152, 0, 0.85)' },
          { ratio: 1,    color: 'rgba(244, 67, 54, 1)' },
        ],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildVirtualPoints(geometries: any[]): Graphic[] {
    const out: Graphic[] = [];
    let oid = 1;
    for (const entry of geometries || []) {
      const weight = Math.max(1, Number(entry?.quantity) || 1);
      const virtuals = this.virtualPointsFor(entry);
      if (virtuals.length === 0) continue;
      for (const p of virtuals) {
        out.push(new Graphic({
          geometry: p,
          attributes: { OBJECTID: oid++, weight },
        }));
      }
    }
    return out;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private virtualPointsFor(entry: any): Point[] {
    // Data flow flattens GeoJSON into { type, coordinates, ... } at the
    // entry root; fall back to entry.geometry for any legacy shape.
    const geom = (entry?.type && entry?.coordinates) ? entry : entry?.geometry;
    if (!geom || !geom.type || !geom.coordinates) return [];
    try {
      if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        return this.densifyLine(geom);
      }
      if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
        return this.samplePolygon(geom);
      }
      if (geom.type === 'Point') {
        const pt = this.toPoint(geom.coordinates);
        return pt ? [pt] : [];
      }
    } catch (err) {
      console.warn('[MapHeatmapContributions] failed to process geometry', err, entry);
    }
    return [];
  }

  private densifyLine(geom: { type: string; coordinates: number[][] | number[][][] }): Point[] {
    const paths: number[][][] = geom.type === 'MultiLineString'
      ? (geom.coordinates as number[][][])
      : [geom.coordinates as number[][]];
    const polyline = new Polyline({
      paths,
      spatialReference: { wkid: 4326 },
    });
    const mercator = webMercatorUtils.geographicToWebMercator(polyline) as __esri.Polyline;
    const densified = geometryEngine.densify(mercator, DENSIFY_METERS, 'meters') as __esri.Polyline;
    const pts: Point[] = [];
    for (const path of densified.paths) {
      for (const [x, y] of path) {
        if (pts.length >= MAX_POINTS_PER_GEOM) {
          console.warn('[MapHeatmapContributions] line geometry exceeded point cap, truncating');
          return pts;
        }
        pts.push(new Point({ x, y, spatialReference: { wkid: 102100 } }));
      }
    }
    return pts;
  }

  private samplePolygon(geom: { type: string; coordinates: number[][][] | number[][][][] }): Point[] {
    const rings: number[][][] = geom.type === 'MultiPolygon'
      ? ([] as number[][][]).concat(...(geom.coordinates as number[][][][]))
      : (geom.coordinates as number[][][]);
    const polygon = new Polygon({
      rings,
      spatialReference: { wkid: 4326 },
    });
    const mercator = webMercatorUtils.geographicToWebMercator(polygon) as __esri.Polygon;
    // Densify the boundary
    const densifiedBoundary = geometryEngine.densify(mercator, DENSIFY_METERS, 'meters') as __esri.Polygon;
    const pts: Point[] = [];
    for (const ring of densifiedBoundary.rings) {
      for (const [x, y] of ring) {
        if (pts.length >= MAX_POINTS_PER_GEOM) {
          console.warn('[MapHeatmapContributions] polygon boundary exceeded point cap, truncating');
          return pts;
        }
        pts.push(new Point({ x, y, spatialReference: { wkid: 102100 } }));
      }
    }
    // Interior grid sampling at DENSIFY_METERS spacing
    const ext = mercator.extent;
    if (ext) {
      for (let x = ext.xmin; x <= ext.xmax && pts.length < MAX_POINTS_PER_GEOM; x += DENSIFY_METERS) {
        for (let y = ext.ymin; y <= ext.ymax && pts.length < MAX_POINTS_PER_GEOM; y += DENSIFY_METERS) {
          const candidate = new Point({ x, y, spatialReference: { wkid: 102100 } });
          if (geometryEngine.contains(mercator, candidate)) {
            pts.push(candidate);
          }
        }
      }
      if (pts.length >= MAX_POINTS_PER_GEOM) {
        console.warn('[MapHeatmapContributions] polygon interior exceeded point cap, truncating');
      }
    }
    return pts;
  }

  private toPoint(coords: number[]): Point | null {
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const [lng, lat] = coords;
    const pt = new Point({ x: lng, y: lat, spatialReference: { wkid: 4326 } });
    return webMercatorUtils.geographicToWebMercator(pt) as Point;
  }
}
